/**
 * @fileoverview Modal for user blocking with justification
 * Uses DmsStatusChangeModal for consistent behavior across the application
 */
import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { intl } from "dmsbasereact";
import { DmsStatusChangeModal } from "../DmsStatusChangeModal";

/**
 * Build payload for user block API
 * @param {string|number} reasonPk - Selected reason PK (or "OTHER" for custom)
 * @param {string} customReason - Custom reason text (for "Other")
 * @param {string} newStatus - New status value (not used for block API)
 * @param {Array} reasonOptions - Available reason options from DmsStatusChangeModal
 * @returns {object} Payload for block API
 */
const buildUserBlockPayload = (reasonPk, customReason, newStatus, reasonOptions) => {
  // If "OTHER" is selected or custom reason is provided, use custom reason
  if (reasonPk === "OTHER" || (customReason && customReason.trim())) {
    return {
      reason: customReason?.trim() || null,
    };
  }

  // Otherwise, look up the selected reason's description
  if (reasonPk && reasonOptions && reasonOptions.length > 0) {
    const selectedReason = reasonOptions.find(r => Number(r.pk) === Number(reasonPk));
    if (selectedReason) {
      return {
        reason: selectedReason.description || selectedReason.name,
      };
    }
  }

  return {
    reason: null,
  };
};

/**
 * Modal for blocking a user with justification
 */
export const ModalUserBlockReason = ({
  isVisible,
  onClose,
  userData = null,
  onSuccess = null}) => {
  const handleSuccess = useCallback(() => {
    onSuccess?.();
  }, [onSuccess]);

  if (!userData) return null;

  return (
    <DmsStatusChangeModal
      open={isVisible}
      onClose={onClose}
      entityPk={userData.pk}
      entityName={`${userData.user_name} (${userData.user_login})`}
      currentStatus="ACTIVE"
      newStatus="BLOCKED"
      apiEndpoint="/user_management"
      apiMethod="POST"
      apiPathSuffix="/block"
      buildPayload={buildUserBlockPayload}
      onSuccess={handleSuccess}
      title={intl.get("BlockUser").d("Block User")}
      statusLabels={{
        ACTIVE: intl.get("Active").d("Active"),
        BLOCKED: intl.get("Blocked").d("Blocked"),
      }}
      reasonLabel={intl.get("BlockReason").d("Reason for Blocking")}
      reasonRequired={true}
      reasonType="user_block_reason"
      warningMessage={intl.get("BlockUserWarning").d("After blocking this user, the password will be reset. A new password will be generated and sent to the user's email address when unblocked.")}
    />
  );
};

ModalUserBlockReason.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userData: PropTypes.object,
  onSuccess: PropTypes.func,
};

